import React, { useState } from "react";
import { api, errMsg, setToken } from "../lib/api";
import { Lock, Loader2, ShieldCheck, ArrowLeft, Mail, Eye, EyeOff, ShieldQuestion } from "lucide-react";

export const PwdInput = ({ id, value, onChange, testid, label, autoComplete = "current-password", required = true, t }) => {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      {label && <label className="eyebrow block mb-1.5" htmlFor={id}>{label}</label>}
      <input id={id} data-testid={testid} type={show ? "text" : "password"} required={required} minLength={autoComplete === "new-password" ? 8 : undefined} autoComplete={autoComplete}
        value={value} onChange={onChange} className="inp" placeholder="••••••••" style={{ paddingRight: 40 }} />
      <button type="button" onClick={() => setShow(!show)} title={show ? t.auth.hidePwd : t.auth.showPwd} data-testid={`${testid}-eye`}
        style={{ position: "absolute", right: 10, bottom: 10, background: "none", border: "none", cursor: "pointer", color: "var(--tx3)", padding: 0 }}>
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
};

export default function LoginView({ t, onLogin, lang, setLang, onBack }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [forgot, setForgot] = useState(false);
  const [sent, setSent] = useState(null);
  const [chal, setChal] = useState(null);
  const [rep, setRep] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      const r = await api.login(email, password);
      if (r.challenge) { setChal(r); setRep(""); }
      else { setToken(r.token); onLogin(r.user); }
    }
    catch (ex) { setErr(errMsg(ex, t.auth.failed)); }
    setBusy(false);
  };

  const answer = async (e) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try { const r = await api.verifierQuestion(chal.challenge_token, rep); setToken(r.token); onLogin(r.user); }
    catch (ex) { setErr(errMsg(ex)); }
    setBusy(false);
  };

  const sendReset = async (e) => {
    e.preventDefault(); setBusy(true); setErr(null); setSent(null);
    try { const r = await api.forgotPassword(email); setSent(r.message || t.auth.forgotSent); }
    catch (ex) { setErr(errMsg(ex)); }
    setBusy(false);
  };

  return (
    <div style={{ minHeight: "100vh", position: "relative", display: "grid", gridTemplateColumns: "1fr", alignItems: "center", overflow: "hidden" }} data-testid="login-page">
      <div aria-hidden style={{ position: "absolute", inset: 0, backgroundImage: "url(https://images.unsplash.com/photo-1579154204601-01588f351e67?w=1800&q=70)", backgroundSize: "cover", backgroundPosition: "center", filter: "blur(2px) saturate(.7)", transform: "scale(1.04)" }} />
      <div aria-hidden style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(255,255,255,0.86) 0%, rgba(255,255,255,0.80) 60%, rgba(255,255,255,0.92) 100%)" }} />
      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" style={{ position: "relative" }}>
        <div className="lg:col-span-7 fade-up">
          <div className="flex items-center gap-3 mb-8">
            <img src="/fhi360.svg" alt="FHI 360" style={{ height: 48, width: 48, objectFit: "contain" }} />
            <div><div className="font-h text-2xl">{t.appName}</div><div className="text-xs" style={{ color: "var(--tx3)" }}>{t.subtitle}</div></div>
          </div>
          {onBack && <button type="button" className="tab mb-4" onClick={onBack} data-testid="login-back-home"><ArrowLeft size={13} className="inline mr-1" />{t.back}</button>}
          <div className="eyebrow mb-6">{t.auth.signIn}</div>
          <h1 className="font-h text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-2xl">{t.auth.hero}</h1>
          <p className="mt-5 text-base max-w-lg" style={{ color: "var(--tx2)" }}>{t.auth.heroSub}</p>
          <div className="mt-8 flex flex-wrap gap-2">
            {t.auth.roles.map((r) => <span key={r} className="badge" style={{ background: "var(--card2)", color: "var(--tx2)", border: "1px solid var(--line)" }}><ShieldCheck size={11} />{r}</span>)}
          </div>
        </div>
        <form onSubmit={chal ? answer : forgot ? sendReset : submit} className="lg:col-span-5 card fade-up" style={{ padding: 32, animationDelay: "120ms", background: "rgba(255,255,255,0.92)", backdropFilter: "blur(8px)" }} data-testid="login-form">
          <div className="eyebrow mb-2">{chal ? t.secq.checkTitle : forgot ? t.auth.forgotTitle : t.auth.signIn}</div>
          <h2 className="font-h font-bold text-2xl mb-6">{chal ? t.secq.checkHead : forgot ? t.auth.forgotHead : t.auth.welcome}</h2>
          {chal ? (
            <>
              <p className="text-sm mb-5" style={{ color: "var(--tx2)" }}>{t.secq.checkSub}</p>
              <div className="card mb-4" style={{ padding: 14, background: "var(--card2)" }}>
                <div className="eyebrow mb-1 flex items-center gap-1.5"><ShieldQuestion size={12} />{t.secq.question}</div>
                <div className="text-sm font-m" data-testid="challenge-question">{chal.question}</div>
              </div>
              <label className="eyebrow block mb-1.5" htmlFor="rep">{t.secq.answer}</label>
              <input id="rep" className="inp mb-5" required value={rep} onChange={(e) => setRep(e.target.value)} data-testid="challenge-answer-input" />
            </>
          ) : (
            <>
              {forgot && <p className="text-sm mb-5" style={{ color: "var(--tx2)" }}>{t.auth.forgotSub}</p>}
              <label className="eyebrow block mb-1.5" htmlFor="email">{t.auth.email}</label>
              <input id="email" data-testid="login-email-input" type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className="inp mb-4" placeholder="prenom.nom@fhi360.org" />
              {!forgot && <div className="mb-5"><PwdInput t={t} id="password" label={t.auth.password} testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} /></div>}
            </>
          )}
          {err && <div className="text-sm mb-4 rounded-sm px-3 py-2" style={{ background: "rgba(220,38,38,0.08)", color: "var(--red)" }} data-testid="login-error">{err}</div>}
          {sent && <div className="text-sm mb-4 rounded-sm px-3 py-2" style={{ background: "rgba(5,150,105,0.08)", color: "var(--green)" }} data-testid="forgot-sent">{sent}</div>}
          <button className="btn btn-primary w-full justify-center" disabled={busy} data-testid={chal ? "challenge-submit-button" : forgot ? "forgot-submit-button" : "login-submit-button"}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : chal ? <ShieldQuestion size={16} /> : forgot ? <Mail size={16} /> : <Lock size={16} />}{chal ? t.secq.verify : forgot ? t.auth.forgotSubmit : t.auth.submit}
          </button>
          <button type="button" className="tab w-full justify-center mt-3" onClick={() => { if (chal) { setChal(null); setPassword(""); } else setForgot(!forgot); setErr(null); setSent(null); }} data-testid="forgot-toggle">
            {chal || forgot ? t.auth.backToLogin : t.auth.forgotLink}
          </button>
          <div className="flex items-center justify-between mt-6 text-xs" style={{ color: "var(--tx3)" }}>
            <span>{t.auth.noPublic}</span>
            <button type="button" className="tab" onClick={() => setLang(lang === "fr" ? "en" : "fr")} data-testid="login-language-toggle">{t.lang}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
